import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StaffApi } from 'api';
import type { Staff } from 'api/methods/StaffApi';

type Return = {
  status: number;
  message: string;
  data: Staff | Staff[];
};

/**
 * [Staff] Query key 생성
 * --
 */
export const staffQueryKeys = createQueryKeys('staff', {
  getStaff: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getStaffs: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Staff] 스태프 전체 조회
 * --
 */
export const useGetStaffs = (params?: { [key: string]: string | number }) => {
  const data = useQuery({
    queryKey: staffQueryKeys.getStaffs(params).queryKey,
    queryFn: () => StaffApi.getStaffs(params),
  });
  return data;
};

/**
 * [Staff] 스태프 상세 조회
 * --
 */
export const useGetStaff = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: staffQueryKeys.getStaff(idx, params).queryKey,
    queryFn: () => StaffApi.getStaff(idx, params),
  });

  return data;
};

/**
 * [Staff] 스태프 생성
 * --
 */
export const useCreateStaff = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: createStaff, ...result } = useMutation({
    mutationFn: (variables: Staff) => StaffApi.createStaff(variables),
    onSuccess: (data, variables) => {
      const queryCache = queryClient.getQueryCache();

      // staff로 저장된 모든 queryKey 가져오기
      const queries = queryCache.findAll({
        queryKey: ['staff'],
      });

      // 캐싱된 데이터 업데이트
      const { data: resultData } = data;
      queries.forEach((_item) => {
        queryClient.setQueryData(_item.queryKey, (prev: Return) => {
          if (!prev) {
            return prev;
          }

          const { status, message, data } = prev;

          let newData;
          if (Array.isArray(data)) {
            newData = [...data, resultData];
          } else {
            newData = resultData;
          }

          return {
            status,
            message,
            data: newData,
          };
        });
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createStaff, ...result };
};

/**
 * [Staff] 스태프 수정
 * --
 */
export const useUpdateStaff = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: updateStaff, ...result } = useMutation({
    mutationFn: (variables: Staff) => StaffApi.updateStaff(variables),
    onSuccess: (data, variables) => {
      const queryCache = queryClient.getQueryCache();

      // staff로 저장된 모든 queryKey 가져오기
      const queries = queryCache.findAll({
        queryKey: ['staff'],
      });

      // 캐싱된 데이터 업데이트
      const { data: resultData } = data;
      queries.forEach((_item) => {
        queryClient.setQueryData(_item.queryKey, (prev: Return) => {
          if (!prev) {
            return prev;
          }

          const { status, message, data } = prev;

          let newData;
          if (Array.isArray(data)) {
            const fid = data.findIndex(
              (item) => item.staff_id === resultData.staff_id
            );
            newData = [
              ...data.slice(0, fid),
              resultData,
              ...data.slice(fid + 1),
            ];
          } else {
            newData = resultData;
          }

          return {
            status,
            message,
            data: newData,
          };
        });
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateStaff, ...result };
};

/**
 * [Staff] 스태프 삭제
 * --
 */
export const useDeleteStaff = () => {
  const { mutateAsync: deleteStaff, ...result } = useMutation({
    mutationFn: (variables: number) => StaffApi.deleteStaff(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteStaff, ...result };
};
